import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertTriangle, Car, Phone, MapPin, User, Sparkles, ArrowRight, Locate, Clock, CalendarClock, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import VehicleCards from "@/components/VehicleCards";

/**
 * PublicHotelBooking
 * ────────────────────────────────────────────────────────────────────
 * Zero-account booking widget for guests landing via the hotel's
 * shareable URL (https://saor.online/h/<slug>).
 */
export default function PublicHotelBooking() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Promo mode = URL starts with /p/. Hotel mode = /h/.
  const isPromo = (location.pathname || "").startsWith("/p/");
  const baseUrl = isPromo ? `/public/promo/${slug}` : `/public/hotel/${slug}`;
  const [hotel, setHotel] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_phone: "",
    departure_address: "",
    departure_coord: null,
    arrival_address: "",
    arrival_coord: null,
    passenger_count: 1,
    luggage_count: 0,
    vehicle_type: "sedan",
    booking_type: "immediate", // "immediate" | "scheduled"
    scheduled_at: "",
    notes: "",
  });

  // Cap sedan to 3 passengers
  useEffect(() => {
    if (form.vehicle_type === "sedan" && Number(form.passenger_count) > 3) {
      setForm((prev) => ({ ...prev, passenger_count: 3 }));
    }
  }, [form.vehicle_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live price preview — debounced 600 ms. Fires whenever we have a
  // destination address (with or without coords) and a vehicle type.
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  useEffect(() => {
    if (!form.arrival_address.trim()) { setQuote(null); return; }
    let alive = true;
    setQuoting(true);
    const tId = setTimeout(async () => {
      try {
        const customDeparture = form.departure_address.trim() && form.departure_address.trim() !== (hotel?.hotel_address || "").trim();
        const { data } = await api.post(`${baseUrl}/quote`, {
          client_name: "preview", client_phone: "preview",
          arrival_address: form.arrival_address.trim(),
          arrival_coord: form.arrival_coord,
          departure_address: customDeparture ? form.departure_address.trim() : null,
          departure_coord: customDeparture ? form.departure_coord : null,
          vehicle_type: form.vehicle_type,
          passenger_count: form.vehicle_type === "sedan" ? Math.min(form.passenger_count, 3) : form.passenger_count,
        });
        if (alive) setQuote(data);
      } catch (_) {
        if (alive) setQuote(null);
      } finally {
        if (alive) setQuoting(false);
      }
    }, 600);
    return () => { alive = false; clearTimeout(tId); };
  }, [slug, baseUrl, hotel?.hotel_address, form.arrival_address, form.arrival_coord?.lng, form.arrival_coord?.lat, form.departure_address, form.departure_coord?.lng, form.departure_coord?.lat, form.vehicle_type, form.passenger_count]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(baseUrl);
        if (!cancelled) {
          setHotel(data);
          setForm((prev) => ({ ...prev, departure_address: data?.hotel_address || "" }));
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e) || "Lien introuvable ou désactivé.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [baseUrl]);

  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non disponible sur cet appareil.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lng = pos.coords.longitude;
        const lat = pos.coords.latitude;
        try {
          const { data } = await api.get(`/geocode/reverse?lng=${lng}&lat=${lat}`);
          setForm((prev) => ({
            ...prev,
            departure_address: data?.place_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            departure_coord: { lng, lat },
          }));
          toast.success("Position détectée.");
        } catch {
          setForm((prev) => ({
            ...prev,
            departure_address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            departure_coord: { lng, lat },
          }));
          toast.success("Position détectée (coordonnées GPS).");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        toast.error(err?.code === 1 ? "Autorisation refusée — saisissez l'adresse manuellement." : "Impossible d'obtenir la position.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.client_phone.trim() || !form.arrival_address.trim()) {
      toast.error("Merci de renseigner votre prénom, téléphone et destination.");
      return;
    }
    if (form.booking_type === "scheduled" && !form.scheduled_at) {
      toast.error("Choisissez une date et une heure pour la course programmée.");
      return;
    }
    if (form.vehicle_type === "sedan" && Number(form.passenger_count) > 3) {
      toast.error("La berline est limitée à 3 passagers — choisissez un van pour plus.");
      return;
    }
    setSubmitting(true);
    try {
      const customDeparture = form.departure_address.trim() && form.departure_address.trim() !== (hotel.hotel_address || "").trim();
      const scheduledIso = form.booking_type === "scheduled" && form.scheduled_at
        ? new Date(form.scheduled_at).toISOString()
        : null;
      const { data } = await api.post(`${baseUrl}/bookings`, {
        client_name: form.client_name.trim(),
        client_phone: form.client_phone.trim(),
        arrival_address: form.arrival_address.trim(),
        arrival_coord: form.arrival_coord,
        departure_address: customDeparture ? form.departure_address.trim() : null,
        departure_coord: customDeparture ? form.departure_coord : null,
        passenger_count: Number(form.passenger_count) || 1,
        luggage_count: Number(form.luggage_count) || 0,
        vehicle_type: form.vehicle_type,
        booking_type: form.booking_type,
        scheduled_at: scheduledIso,
        notes: form.notes.trim() || null,
      });
      toast.success("Réservation envoyée — vous allez être redirigé vers le suivi.");
      if (data?.tracking_token) {
        navigate(`/track/${data.tracking_token}`, { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Réservation impossible — réessayez dans un instant.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-[70vh] place-items-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid min-h-[70vh] place-items-center p-6">
        <div className="max-w-md rounded-3xl border-2 border-destructive/30 bg-destructive/5 p-8 text-center" data-testid="public-booking-error">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-destructive" />
          <h1 className="font-[Outfit] text-xl font-medium text-foreground">Lien indisponible</h1>
          <p className="mt-3 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const isSedan = form.vehicle_type === "sedan";
  const maxPax = isSedan ? 3 : 8;
  const minScheduledAt = (() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  })();

  // Accent color used everywhere on the page when the admin configured one
  const accent = hotel.accent_color || null;
  const accentStyle = accent ? { backgroundColor: accent } : {};
  const accentTextStyle = accent ? { color: accent } : {};
  const accentBorderStyle = accent ? { borderColor: accent } : {};
  // Soft tint of the accent color for backgrounds (with alpha)
  const accentSoftStyle = accent
    ? { backgroundColor: `${accent}1A`, color: accent, borderColor: `${accent}40` }
    : {};

  // Editable labels with sensible French defaults
  const partnerLabel = (hotel.partner_label || "").trim()
    || (hotel.account_type === "airbnb" ? "Airbnb partenaire" : "Hôtel partenaire");
  const subtitle = (hotel.subtitle || "").trim()
    || "Pas besoin de compte, c'est instantané.";
  const footerMessage = (hotel.footer_message || "").trim()
    || "Paiement à bord. Vous recevrez un lien de suivi en temps réel dès qu'un chauffeur acceptera la course.";

  const region = hotel.region || "paca";
  const hotelCoord = hotel.coord || null;

  return (
    <div className="relative min-h-screen" data-testid="public-booking-form">
      {hotel.background_image_url && (
        <>
          <div
            className="absolute inset-0 -z-10 bg-cover bg-center"
            style={{ backgroundImage: `url(${hotel.background_image_url})` }}
            data-testid="public-booking-bg"
          />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/30 via-black/50 to-background/95" />
        </>
      )}
      <div className="mx-auto max-w-2xl px-4 py-10 sm:py-14">
        <div className="mb-8 text-center">
          {hotel.logo_url ? (
            <img
              src={hotel.logo_url}
              alt="Logo"
              className="mx-auto mb-5 h-28 w-auto rounded-2xl bg-white p-3 shadow-xl sm:h-32"
              data-testid="public-booking-logo"
            />
          ) : (
            <div
              className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-2xl text-white shadow-xl"
              style={accent ? accentStyle : { backgroundColor: "hsl(var(--primary))" }}
            >
              <Sparkles className="h-10 w-10" />
            </div>
          )}
          <p
            className="inline-block rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={accent ? accentSoftStyle : undefined}
            data-testid="public-booking-partner-label"
          >
            {partnerLabel}
          </p>
          <h1
            className={`mt-3 font-[Outfit] text-3xl font-light tracking-tight sm:text-4xl ${hotel.background_image_url ? "text-white drop-shadow-lg" : ""}`}
            style={!hotel.background_image_url && accent ? accentTextStyle : undefined}
          >
            {hotel.welcome_message || `Réservation Saor — ${hotel.hotel_name}`}
          </h1>
          <p className={`mt-2 text-sm ${hotel.background_image_url ? "text-white/90 drop-shadow" : "text-muted-foreground"}`}>
            {subtitle}
          </p>
          {hotel.discount_percent > 0 && (
            <div
              className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 shadow-sm"
              style={accent ? { borderColor: accent, backgroundColor: `${accent}1A`, color: accent } : { borderColor: "hsl(var(--primary))", color: "hsl(var(--primary))" }}
              data-testid="public-booking-discount-badge"
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold">
                {hotel.discount_label || "Tarif partenaire"} : −{hotel.discount_percent}% sur toutes les courses
              </span>
            </div>
          )}
        </div>

        <form
          onSubmit={submit}
          className="space-y-5 rounded-3xl border bg-card p-6 shadow-sm"
          style={accent ? { borderColor: `${accent}33` } : undefined}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs"><User className="mr-1 inline h-3 w-3" /> Prénom *</Label>
              <Input
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="Ex: Audrey"
                required
                data-testid="public-booking-name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs"><Phone className="mr-1 inline h-3 w-3" /> Téléphone *</Label>
              <Input
                type="tel"
                value={form.client_phone}
                onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                placeholder="06 12 34 56 78"
                required
                data-testid="public-booking-phone"
                className="mt-1"
              />
            </div>
          </div>

          {/* Departure — autocomplete + prominent geolocation button */}
          <div>
            <Label className="text-xs"><MapPin className="mr-1 inline h-3 w-3" /> Départ *</Label>
            <div className="mt-1">
              <AddressAutocomplete
                value={form.departure_address}
                onChange={(v) => setForm({ ...form, departure_address: v, departure_coord: null })}
                onSelect={(v, coord) => setForm({ ...form, departure_address: v, departure_coord: coord })}
                proximityCoord={hotelCoord}
                region={region}
                placeholder="Adresse de départ"
                required
                data-testid="public-booking-departure"
              />
            </div>
            <button
              type="button"
              onClick={handleGeolocate}
              disabled={geoLoading}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-medium transition hover:shadow-md disabled:opacity-50"
              style={accent ? { borderColor: accent, color: accent, backgroundColor: `${accent}0D` } : { borderColor: "hsl(var(--primary))", color: "hsl(var(--primary))" }}
              data-testid="public-booking-geolocate"
            >
              {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />}
              Utiliser ma position actuelle
            </button>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Par défaut depuis l'hôtel. Modifiez si vous êtes ailleurs.
            </p>
          </div>

          <div>
            <Label className="text-xs"><MapPin className="mr-1 inline h-3 w-3" /> Destination *</Label>
            <div className="mt-1">
              <AddressAutocomplete
                value={form.arrival_address}
                onChange={(v) => setForm({ ...form, arrival_address: v, arrival_coord: null })}
                onSelect={(v, coord) => setForm({ ...form, arrival_address: v, arrival_coord: coord })}
                proximityCoord={hotelCoord}
                region={region}
                placeholder="Aéroport, gare, restaurant…"
                required
                data-testid="public-booking-destination"
              />
            </div>
          </div>

          {/* Immediate vs Scheduled toggle */}
          <div>
            <Label className="text-xs">Quand ?</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, booking_type: "immediate", scheduled_at: "" })}
                className="flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition"
                style={
                  form.booking_type === "immediate"
                    ? (accent ? { borderColor: accent, backgroundColor: `${accent}1A`, color: accent, fontWeight: 500 } : undefined)
                    : undefined
                }
                data-testid="public-booking-mode-immediate"
              >
                <Clock className="h-4 w-4" /> Immédiat
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, booking_type: "scheduled" })}
                className="flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition"
                style={
                  form.booking_type === "scheduled"
                    ? (accent ? { borderColor: accent, backgroundColor: `${accent}1A`, color: accent, fontWeight: 500 } : undefined)
                    : undefined
                }
                data-testid="public-booking-mode-scheduled"
              >
                <CalendarClock className="h-4 w-4" /> Programmé
              </button>
            </div>
            {form.booking_type === "scheduled" && (
              <Input
                type="datetime-local"
                value={form.scheduled_at}
                min={minScheduledAt}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                required
                className="mt-2"
                data-testid="public-booking-scheduled-at"
              />
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 block text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">Choisir un véhicule</Label>
              <VehicleCards
                value={form.vehicle_type}
                onChange={(v) => setForm({ ...form, vehicle_type: v })}
                testIdPrefix="public-booking-vehicle"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Passagers (max {maxPax})</Label>
                {/* Use a native <select> instead of <input type="number"> to
                    avoid the mobile-keyboard concatenation bug: when the
                    field showed "1" and the user tapped "3" it turned into
                    "13" and got clamped to maxPax (8 for van). */}
                <select
                  value={form.passenger_count}
                  onChange={(e) => setForm({ ...form, passenger_count: Number(e.target.value) || 1 })}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="public-booking-pax"
                >
                  {Array.from({ length: maxPax }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n} {n > 1 ? "passagers" : "passager"}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Bagages</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={form.luggage_count}
                  onChange={(e) => setForm({ ...form, luggage_count: e.target.value })}
                  className="mt-1"
                  data-testid="public-booking-luggage"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">Note pour le chauffeur (optionnel)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ex: bagages volumineux, siège bébé…"
              className="mt-1"
              data-testid="public-booking-notes"
            />
          </div>

          {/* Live price preview — refreshes every time the user changes
              destination, departure, vehicle… Before final submission. */}
          {(quote || quoting) && form.arrival_address.trim() && (
            <div
              className="rounded-2xl border p-4"
              style={accent ? { borderColor: `${accent}40`, backgroundColor: `${accent}0A` } : { borderColor: "hsl(var(--primary))" }}
              data-testid="public-booking-quote"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest" style={accent ? accentTextStyle : undefined}>
                    <Receipt className="mr-1 inline h-3 w-3" /> Tarif estimé
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {quote?.fixed_zone_label
                      ? `Forfait ${quote.fixed_zone_label}`
                      : quote?.distance_km
                        ? `≈ ${Math.round(quote.distance_km)} km · au tarif kilométrique`
                        : "Calcul en cours…"}
                    {quote?.event_surge_pct ? ` · ${quote.event_surge_pct > 0 ? "+" : ""}${quote.event_surge_pct}%` : ""}
                  </p>
                </div>
                <div className="flex-none text-right">
                  {quoting && !quote ? (
                    <Loader2 className="h-6 w-6 animate-spin" style={accent ? accentTextStyle : undefined} />
                  ) : quote?.price != null ? (
                    <>
                      {quote.discount_percent > 0 && quote.original_price > quote.price && (
                        <p className="text-xs text-muted-foreground line-through" data-testid="public-booking-quote-original">
                          {Math.round(quote.original_price)} €
                        </p>
                      )}
                      <p className="font-[Outfit] text-3xl font-medium leading-none" style={accent ? accentTextStyle : undefined} data-testid="public-booking-quote-price">
                        {Math.round(quote.price)} €
                      </p>
                    </>
                  ) : null}
                </div>
              </div>
              {quote?.discount_percent > 0 && (
                <div
                  className="mt-3 flex items-center justify-between gap-2 rounded-lg border-2 border-dashed px-3 py-1.5"
                  style={accent ? { borderColor: `${accent}55`, backgroundColor: `${accent}14` } : { borderColor: "hsl(var(--primary)/0.4)" }}
                  data-testid="public-booking-quote-discount"
                >
                  <span className="text-xs font-medium" style={accent ? accentTextStyle : undefined}>
                    <Sparkles className="mr-1 inline h-3 w-3" /> {hotel.discount_label || "Remise partenaire"} appliquée
                  </span>
                  <span className="font-[Outfit] text-sm font-semibold" style={accent ? accentTextStyle : undefined}>
                    −{quote.discount_percent}% · économisez {Math.round(quote.discount_amount)} €
                  </span>
                </div>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full py-6 text-base font-medium text-white hover:opacity-95"
            style={accent ? { backgroundColor: accent } : undefined}
            data-testid="public-booking-submit"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi en cours…</>
            ) : (
              <><Car className="mr-2 h-5 w-5" /> Réserver mon chauffeur <ArrowRight className="ml-2 h-4 w-4" /></>
            )}
          </Button>
          <p className="text-center text-[11px] text-muted-foreground" data-testid="public-booking-footer">
            {footerMessage}
          </p>
        </form>
      </div>
    </div>
  );
}
